import { forwardRef, Fragment, useEffect, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { CloseTab, OverflowLite } from '@sas/react-svg';
import cx from 'classnames';
import { TabBarVariant } from '.';
import { setAlpha } from '../../../design-tokens';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { useNumberToWords } from '../../../hooks/useNumberToWords';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { isTruncated, useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderDesignTokens, renderInlineCssVars, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { ContextMenu } from '../../ContextMenu';
import { useDensity } from '../../Density';
import { Icon } from '../../Icon';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { Indicator } from '../../Indicator';
import { Input } from '../../Input';
import { InvisibleText } from '../../InvisibleText';
import { useKeyboardShortcuts } from '../../KeyboardShortcuts';
import { Menu } from '../../Menu';
import { useToggleMenu } from '../../MenuButton';
import { Popover } from '../../Popover';
import { BaseText } from '../../Text/__internal__';
import { withLabels } from '../../withLabels';
import bundle from '../NovaComponents-Tab-gui-icu.properties';
import cn from './Tab.css.json';
import './Tab.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useLabelId = createUseHtmlId('TabBar-label');
const useIconId = createUseHtmlId('TabBar-icon');
const useTabStateId = createUseHtmlId('Tab-state');
const useCloseTabId = createUseHtmlId('Tab-close');
const useNonMovableTabId = createUseHtmlId('Tab-non-movable');
const InputWithLabel = withLabels(Input);

/**
 * Don't use Tab outside of Tabbar, please use the Tabbar instead.
 */
export const Tab = /*#__PURE__*/forwardRef(function Tab(props, ref) {
  const localize = useTextLocalizer();
  const refTab = useRef(null);
  const inputRef = useRef(null);
  const menuBtnRef = useRef(null);
  const textRef = useRef(null);
  const isEscapeKeyPressed = useRef(false);
  const selected = props.selected;
  const tokenOverrides = props.tokenOverrides;
  const alternate = props.visualStyle === 'alternative';
  const standardSmall = props.visualStyle === 'standard-small';
  const variant = props.variant ?? TabBarVariant.Standard;
  const orientation = props.orientation ?? 'horizontal';
  const hidden = props.hidden;
  const state = props.state ? props.state : hidden ? 'hidden' : undefined;
  const count = props.count ?? undefined;
  const hideTabMenuButton = props.hideTabMenuButton;
  const maxTabWidthDefault = 25;
  const maxTabWidth = props.maxTabWidth ?? maxTabWidthDefault;
  const dt = useDesignTokens();
  const {
    getActionsByKeyEvent
  } = useKeyboardShortcuts();
  const stateArray = !isNullish(state) && !Array.isArray(state) ? [state] : state ?? [];
  const [isInEditMode, setEditMode] = useState(false);
  const [editedLabel, setEditedLabel] = useState(props.label);
  const [isEditedLabelPopoverOpen, setIsEditedLabelPopoverOpen] = useState(false);
  const [defaultFocusLocation, setDefaultFocusLocation] = useState();
  const labelValue = props.label ?? '';
  const minTabWidth = Number(localize('tabBar.minimumCharactersTab.lcl'));
  const renameMenuItemtext = localize('tab.contextMenu.Rename.label');
  const closeTabMenuItemText = props.closeTabMenuItemText ?? localize('tab.closeTabMenuItem.label');
  const closeAllTabMenuItemText = props.closeAllTabMenuItemText ?? localize('tab.closeAllTabMenuItem.label');
  const renameMenuItemPosition = props.renameMenuItemPosition ?? 0;
  const showRenameMenuOption = props.showRenameMenuOption ?? true;
  const showCloseMenuOption = props.showCloseMenuOption ?? true;
  const hideCloseAllTabOption = props.hideCloseAllTabOption;
  const showContextMenu = props.showContextMenu;
  const setTextRef = useTruncationTooltip({
    tooltip: props.labelTooltip,
    truncateRef: textRef
  });
  const {
    density
  } = useDensity();
  const convertNumberToWord = useNumberToWords();
  const prevOrientation = usePreviousValue(orientation);
  const disableTransition = props.orientation !== prevOrientation;

  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const tokens = {
    ...getTabDesignTokens(dt, {
      alternate,
      variant,
      tokenOverrides,
      orientation,
      standardSmall,
      selected,
      minTabWidth,
      density,
      disableTransition,
      isNewTokenValueFlagEnabled
    }),
    ...props.theme?.(dt)
  };
  const labelId = useLabelId();
  const iconId = useIconId();
  const stateTabIds = useTabStateId(stateArray.length);
  const closeTabId = useCloseTabId();
  const nonMovableTabId = useNonMovableTabId();
  const ariaLabel = [];
  const iconAriaLabel = props.icon?.ariaLabel ?? props.icon?.tooltip;
  const textAriaLabel = props.ariaLabel ?? props.label;
  const isTabbarExpanded = props.isExpanded ?? true;
  // Note : Checking for Tab and Icon that they should not have the same name so that Jaws will not announce the name twice.
  if (props.icon && iconAriaLabel && (iconAriaLabel !== props.label || !isTabbarExpanded)) {
    ariaLabel.push(iconAriaLabel);
  }
  if (props.label && textAriaLabel && isTabbarExpanded) {
    ariaLabel.push(textAriaLabel);
  }
  const ariaDescribedBy = [];
  if (stateArray.length > 0) {
    ariaDescribedBy.splice(0, 0, ...stateTabIds);
  }
  if (props.closable) {
    ariaDescribedBy.push(closeTabId);
  }
  if (!props.allowDrag && props.movableTab) {
    ariaDescribedBy.push(nonMovableTabId);
  }
  if (props.ariaDescribedBy) {
    ariaDescribedBy.push(props.ariaDescribedBy);
  }

  // keyboard tooltip
  const getIconA11yInfo = useIconA11yInfo();
  const iconToolTip = props.icon ? getIconA11yInfo(props.icon).tooltip : undefined;
  const closeTabButtonTooltip = props.closeTabButtonkeyboardTooltip ?? localize('tab.closeButtonMessage.aria');
  const getTabTooltip = () => {
    let tooltip = '';
    const isTabLabelTruncated = textRef.current && isTruncated(textRef.current);
    let tabLabelTooltip = '';
    if (isTabLabelTruncated) {
      if (props.labelTooltip && props.label !== props.labelTooltip) {
        tabLabelTooltip = `${props.label}, ${props.labelTooltip}`;
      } else if (props.label) {
        tabLabelTooltip = props.label;
      }
    } else if (props.labelTooltip) {
      tabLabelTooltip = props.labelTooltip;
    }

    // icon only
    if (iconToolTip) {
      tooltip = iconToolTip;
    }

    // icon + text
    if (iconToolTip && props.label) {
      if (props.labelTooltip && props.labelTooltip !== '' && props.labelTooltip !== props.label) {
        tooltip += `, ${props.label}, ${props.labelTooltip}`;
      } else if (tooltip !== props.label) {
        tooltip += `, ${props.label}`;
      }
    } else {
      tooltip += tabLabelTooltip;
    }

    // context menu
    if (props.editable || props.closable || props.menuProps) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${localize('tab.contextMenuMessage.tooltip.label')}`;
      } else {
        tooltip = localize('tab.contextMenuMessage.tooltip.label');
      }
    }

    // closable
    if (props.closable) {
      tooltip += `\n${closeTabButtonTooltip}`;
    }

    // draggable
    if (props.allowDrag && !props.disabled) {
      if (tooltip.length > 0) {
        tooltip += `\n${localize('tab.movable.shortcutKey.tooltip.label')}`;
      } else {
        tooltip = localize('tab.movable.shortcutKey.tooltip.label');
      }
    }
    return tooltip;
  };
  const setKeyboardTooltipRef = useKeyboardTooltip({
    getTooltip: getTabTooltip
  });
  const getMenuSections = () => {
    const itemMenuSections = [...(props.menuProps?.menuSections ?? [])];
    if (props.editable && showRenameMenuOption && props.label && props.label.length > 0) {
      const index = renameMenuItemPosition < 0 || itemMenuSections.length <= renameMenuItemPosition ? 0 : renameMenuItemPosition;
      itemMenuSections.splice(index, 0, {
        items: [{
          text: renameMenuItemtext
        }]
      });
    }
    if (props.closable && showCloseMenuOption) {
      itemMenuSections.push({
        items: [{
          text: closeTabMenuItemText
        }]
      });
    }
    if (props.closable && hideCloseAllTabOption) {
      itemMenuSections.push({
        items: [{
          text: closeAllTabMenuItemText
        }]
      });
    }
    return itemMenuSections;
  };
  const onItemSelected = location => {
    const selectedMenuText = menuSections?.[location[0][0]].items[location[0][1]].text;
    if (props.editable && location[0][0] === renameMenuItemPosition) {
      setIsEditedLabelPopoverOpen(!isEditedLabelPopoverOpen);
      setEditMode(!!props.editable);
    } else if (selectedMenuText === closeAllTabMenuItemText) {
      props.onCloseAll?.();
    } else if (props.closable && selectedMenuText === closeTabMenuItemText) {
      props.onClose?.();
    } else if (props.menuProps) {
      props.menuProps?.onMenuItemSelect(location, menuSections);
    }
  };
  const {
    handleDismiss,
    handlePress,
    setMenuIsOpen,
    menuIsOpen
  } = useToggleMenu({
    disabled: false,
    onDismiss: () => {
      refTab.current?.focus();
      setMenuIsOpen(false);
    },
    onOpen: () => {
      if (props.menuProps && props.menuProps.onOpen) {
        props.menuProps.onOpen();
      }
      setDefaultFocusLocation(undefined);
    }
  });
  useEffect(() => {
    const tabContextMenu = document.querySelector("[data-testid^='tab-contextMenu']");
    if (props.focused) {
      if (props.preventInitialAutoFocus) {
        props.setPreventInitialAutoFocus?.(false);
      } else if (isInEditMode) {
        inputRef.current?.focus();
      } else if (tabContextMenu === null) {
        refTab.current?.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focused, isInEditMode]);
  useEffect(() => {
    setEditedLabel(undefined);
  }, [isInEditMode]);
  function isMenuButtonClicked(e) {
    return e.target === menuBtnRef.current || menuBtnRef.current?.contains(e.target);
  }
  const menuSections = getMenuSections();
  const showStateIndicator = Array.isArray(state) ? state?.includes('hidden') || state?.includes('processing') : state === 'processing' || state === 'hidden';
  const showReadOnlyIndicator = !props.label && orientation === 'vertical' ? false : Array.isArray(state) ? state?.includes('readOnly') : state === 'readOnly';
  const getTabItemMaxWidth = renderInlineCssVars({
    'max-width': `${Math.min(maxTabWidth, maxTabWidthDefault)}rem`
  }, 'tab');
  const renderTab = () => {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        id: props.id,
        "data-altid": props.altId,
        className: cx(cn.tab, props.menuProps && !hideTabMenuButton && cn.withMenu, !props.label && cn.iconOnly, props.selected && !props.disabled && cn.selected, alternate && cn.alternate, standardSmall && cn.standardSmall, props.closable && cn.withClosable, props.disabled && cn.disabled, props.allowDrag && cn.dragableTab, props.className),
        style: {
          ...tokens,
          ...getTabItemMaxWidth,
          ...props.style
        },
        onClick: e => {
          if (!e.isDefaultPrevented() && !isMenuButtonClicked(e) && !props.disabled) {
            props.onPress?.();
            if (isEditedLabelPopoverOpen) {
              setEditedLabel(undefined);
              setIsEditedLabelPopoverOpen(false);
            }
          }
        },
        onDoubleClick: e => {
          if (props.editable && !props.disabled && !isEditedLabelPopoverOpen && props.label?.length && e.currentTarget.contains(e.target)) {
            setIsEditedLabelPopoverOpen(true);
            setEditMode(true);
          }
        },
        onKeyDown: e => {
          const {
            actions
          } = getActionsByKeyEvent(e.nativeEvent);
          // Do not implement stopPropagation in case of landmark
          if (e.target !== e.currentTarget && !actions.includes('openLandmarks')) {
            e.stopPropagation();
          }
          if (e.key === 'Delete' && props.closable && !props.disabled && !isEditedLabelPopoverOpen) {
            props.onClose?.();
          }
        },
        "data-testid": props.testId,
        "data-sasrc-tablocation": props.location,
        tabIndex: props.tabIndex,
        ref: element => {
          setRef(ref, element);
          refTab.current = element;
          setKeyboardTooltipRef(element);
          if (props.allowDrag && !props.labelTooltip) {
            setTextRef(element);
          }
        },
        ...(props.allowDrag && props.labelTooltip && {
          title: props.labelTooltip
        }),
        onFocus: props.onFocus,
        onBlur: e => {
          if (menuIsOpen) {
            e.stopPropagation();
          }
        },
        role: "tab",
        "aria-selected": props.selected,
        "aria-label": ariaLabel.join(', '),
        "aria-disabled": !!props.disabled,
        "aria-controls": props.ariaControls,
        "aria-describedby": ariaDescribedBy.join(' '),
        children: /*#__PURE__*/_jsxs("div", {
          className: cn.tabInner,
          children: [props.icon && !showStateIndicator && /*#__PURE__*/_jsx("span", {
            className: cx(cn.tabIcon, !props.label && cn.tabIconOnly),
            children: /*#__PURE__*/_jsx(Icon, {
              ...props.icon,
              className: cx(cn.icon, props.icon.className),
              tokens: () => {
                return {
                  'default-fg': props.disabled ? undefined : 'var(--tab-fg)',
                  fg: props.disabled ? 'var(--tab-disabled-fg)' : undefined
                };
              },
              style: {
                '--icon-height': density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m'],
                '--icon-width': density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m']
              },
              id: iconId,
              ariaLabel: iconAriaLabel
            })
          }), stateArray.map((element, index) => {
            return /*#__PURE__*/_jsx(Fragment, {
              children: element !== 'readOnly' && /*#__PURE__*/_jsxs(Fragment, {
                children: [/*#__PURE__*/_jsx(Indicator, {
                  type: element,
                  count: element === 'count' ? count : undefined,
                  maxDigits: orientation === 'vertical' ? 1 : props.maxDigits ?? 3,
                  className: cn.indicator,
                  testId: postfixTestId(props.testId, `state-${element}`),
                  tooltip: element === 'hidden' ? localize('tab.hidden.ariaLabel.aria') : undefined
                }, element), /*#__PURE__*/_jsx(InvisibleText, {
                  id: stateTabIds[index],
                  children: element === 'count' && count !== undefined ? convertNumberToWord(count) : localize(`tab.${element}.ariaLabel.aria`)
                })]
              })
            }, element);
          }), props.label && props.label.length > 0 && /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(BaseText, {
              testId: postfixTestId(props.testId, 'text'),
              id: labelId,
              wrapText: false,
              className: cn.text,
              ref: element => {
                setTextRef(element);
                setRef(textRef, element);
              },
              children: props.label
            }), /*#__PURE__*/_jsx(Popover, {
              referenceElementRef: refTab,
              isOpen: isEditedLabelPopoverOpen,
              onClose: () => {
                isEscapeKeyPressed.current = false;
                setIsEditedLabelPopoverOpen(false);
              },
              onEscape: () => {
                isEscapeKeyPressed.current = true;
                setEditMode(false);
              },
              role: "dialog",
              ariaLabel: localize('tab.popover.ariaLabel.aria'),
              onOutsideClick: () => {
                setEditedLabel(undefined);
                setEditMode(false);
              },
              children: /*#__PURE__*/_jsx(InputWithLabel, {
                testId: postfixTestId(props.testId, 'input'),
                label: localize('tab.popover.inputLabelName.label'),
                labelPosition: "left",
                fieldSuffixMinWidth: 0,
                inputStyle: {
                  maxWidth: '25rem'
                },
                fieldWidth: '16rem',
                fieldMinWidth: '3rem',
                ref: inputRef,
                value: isInEditMode ? editedLabel ?? labelValue : labelValue,
                onChange: e => {
                  setEditedLabel(e.value);
                  props.onLabelChange?.(e.value, 'Change'); // eslint-disable-line @sas/i18n/jsx-to-localize
                },

                onCommit: e => {
                  if (e.reason === 'blur' && isEscapeKeyPressed.current) return;
                  setEditedLabel(e.value === '' ? labelValue : e.value);
                  props.onLabelChange?.(e.value, 'Commit'); // eslint-disable-line @sas/i18n/jsx-to-localize
                  setEditMode(false);
                  setIsEditedLabelPopoverOpen(false);
                },
                onCancel: () => {
                  setEditedLabel(undefined);
                  props.onLabelChange?.(labelValue, 'Cancel'); // eslint-disable-line @sas/i18n/jsx-to-localize
                  setEditMode(false);
                  setIsEditedLabelPopoverOpen(false);
                },
                onBlur: () => isEscapeKeyPressed.current = false,
                message: props.message,
                status: props.status
              })
            })]
          }), showReadOnlyIndicator && /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(Indicator, {
              type: 'readOnly',
              className: cx(cn.indicator, cn.readOnly),
              testId: postfixTestId(props.testId, `state-${'readOnly'}`)
            }, 'readOnly'), /*#__PURE__*/_jsx(InvisibleText, {
              id: `${stateTabIds}`,
              children: localize(`tab.${'readOnly'}.ariaLabel.aria`)
            })]
          }, 'readOnly'), !isNullish(props.menuProps) && !hideTabMenuButton && /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx("span", {
              className: cn.iconButtonContainer,
              children: /*#__PURE__*/_jsx(Icon, {
                icon: OverflowLite,
                ref: menuBtnRef,
                testId: postfixTestId(props.testId, 'menuBtn'),
                tooltip: props.menuButtonTooltip ?? localize('tab.menuButton.tooltip.label'),
                ariaHidden: true,
                ariaLabel: '',
                tabIndex: -1,
                onDoubleClick: e => {
                  e.preventDefault();
                  e.stopPropagation();
                },
                onClick: handlePress,
                disabled: props.disabled,
                className: cx(cn.iconButton, menuIsOpen && cn.open),
                tokens: () => {
                  return {
                    fg: props.disabled ? 'var(--tab-disabled-fg)' : 'var(--tab-fg)'
                  };
                }
              })
            }), /*#__PURE__*/_jsx(Menu, {
              isOpen: menuIsOpen,
              sections: menuSections,
              onItemSelected: location => {
                onItemSelected(location);
                setMenuIsOpen(false);
              },
              position: {
                dockingElement: menuBtnRef.current
              },
              onDismiss: handleDismiss,
              defaultFocusLocation: defaultFocusLocation
            })]
          }), props.closable && (!props.menuProps || hideTabMenuButton) && /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(InvisibleText, {
              id: closeTabId,
              children: localize('tab.closeButtonMessage.aria')
            }), /*#__PURE__*/_jsx("span", {
              className: cn.iconButtonContainer,
              children: /*#__PURE__*/_jsx(Icon, {
                testId: postfixTestId(props.testId, 'closebutton'),
                icon: CloseTab,
                tooltip: props.closeTabButtonTooltip ?? localize('tab.closeButton.tooltip.label'),
                disabled: props.disabled,
                tabIndex: -1,
                ariaHidden: true,
                onPress: () => {
                  props.onClose?.();
                },
                onClick: e => e.stopPropagation(),
                className: cn.iconButton,
                tokens: () => {
                  return {
                    fg: props.disabled ? 'var(--tab-disabled-fg)' : 'var(--tab-fg)'
                  };
                }
              })
            })]
          })]
        })
      }), !props.allowDrag && props.movableTab && /*#__PURE__*/_jsx(InvisibleText, {
        id: nonMovableTabId,
        children: localize('tab.not.movable.message.aria')
      })]
    });
  };
  return showContextMenu && !props.disabled && menuSections.length > 0 ? /*#__PURE__*/_jsx(ContextMenu, {
    testId: 'tab-contextMenu',
    sections: menuSections,
    onItemSelected: location => onItemSelected(location),
    onContextMenu: props.menuProps?.onOpen,
    children: renderTab()
  }) : renderTab();
});
export const getTabDesignTokens = (dt, opts) => {
  const props = {
    selected: opts?.selected ?? false,
    alternate: opts?.alternate ?? false,
    standardSmall: opts?.standardSmall ?? false,
    variant: opts?.variant ?? TabBarVariant.Standard,
    orientation: opts?.orientation ?? 'horizontal',
    density: opts?.density,
    disableTransition: opts?.disableTransition
  };
  const primaryLight = props.variant === TabBarVariant.PrimaryLight;
  let focusToken = 'var(--focus-light-bg)';
  let themeToken;
  if (dt.enableNovaTokens && primaryLight && dt.themeVariant === 'light' && !(props.alternate && props.selected)) {
    focusToken = 'var(--focus-dark-bg)';
  }
  const tabTokens = {
    bg: props.alternate && props.selected ? dt.tokens['color-container-background-0'] : dt.tokens['color-action-background-subtle-normal'],
    fg: props.selected ? dt.tokens['color-text-primary'] : dt.tokens['color-text-neutral'],
    'count-indicator-fg': dt.tokens['color-text-neutral-inverse'],
    'icon-indicator-fg': dt.tokens['color-action-icon-subtle-selected'],
    bd: dt.tokens['color-container-border-4'],
    'hover-bg': props.selected || props.orientation === 'vertical' ? undefined : dt.tokens['color-action-background-subtle-hover'],
    'selected-indicator': dt.tokens['color-primary'],
    'disabled-bg': 'var(--tab-bg)',
    'disabled-fg': dt.tokens['color-action-text-subtle-disabled'],
    'indicator-content': '""',
    'indicator-width': dt.tokens['size-border-width-s'],
    'editable-hover-bd': 'var(--tab-fg)',
    focus: focusToken,
    'selected-focus': dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg)' : focusToken,
    'trans-duration': props.disableTransition ? '0ms' : dt.tokens['duration-400'],
    'focus-outline-offset': 'calc(-2 * var(--focus-width))',
    ...(!opts?.isNewTokenValueFlagEnabled && {
      'focus-outline-offset': 'calc(-1 * var(--focus-width) - 2px)'
    }),
    'trans-tf-ease-in-out': dt.tokens['ease-in-out']
  };
  const densityTokens = opts?.density === 'high' ? {
    'spacing-tab-items': dt.tokens['space-squish-s'],
    'spacing-items-start': dt.tokens['space-base-null'],
    'item-font-size': dt.tokens['font-size-body-text-s'],
    'item-min-height': '1.92rem'
  } : {
    'spacing-tab-items': dt.tokens['space-squish-m'],
    'spacing-items-start': dt.tokens['space-base-s'],
    'item-font-size': dt.tokens['font-size-body-text-m'],
    'item-min-height': '2.52rem' // adjusted min-height to avoid toggle issue when collapsed
  };

  switch (dt.themeVariant) {
    case 'dark':
      themeToken = {
        ...tabTokens,
        'hover-bg': props.selected || props.orientation === 'vertical' ? undefined : setAlpha(dt.tokens['color-action-background-subtle-hover'], 0.25)
      };
      break;
    case 'high-contrast':
      themeToken = {
        ...tabTokens,
        bd: props.alternate ? dt.tokens['color-container-border-4'] : 'transparent',
        'hover-bg': props.selected || props.orientation === 'vertical' ? undefined : setAlpha(dt.tokens['color-action-background-subtle-hover'], 0.15),
        'indicator-width': dt.tokens['size-border-width-l']
      };
      break;
    default:
      if (primaryLight) {
        themeToken = {
          ...tabTokens,
          fg: props.alternate && props.selected ? dt.tokens['color-action-text-subtle-normal'] : dt.tokens['color-action-text-emphasis-normal'],
          'hover-bg': props.selected || props.orientation === 'vertical' ? undefined : setAlpha(dt.tokens['color-action-icon-emphasis-normal'], 0.15),
          'selected-indicator': props.alternate ? dt.tokens['color-action-icon-subtle-selected'] : dt.tokens['color-action-icon-emphasis-normal'],
          'disabled-bg': dt.tokens['color-container-background-0'],
          'disabled-fg': dt.tokens['color-action-text-emphasis-disabled']
        };
      }
  }
  const token = {
    ...tabTokens,
    ...themeToken,
    ...densityTokens,
    'animation-duration': dt.tokens['duration-100'],
    'animation-timing-function': dt.tokens['ease-in-out'],
    'item-font-weight': dt.tokens['font-style-weight-400'],
    'font-size-s': dt.tokens['font-size-body-text-s'],
    'icon-hover-fg': dt.tokens['color-action-icon-subtle-hover'],
    'icon-hover-bg': dt.tokens['color-action-background-subtle-hover'],
    'icon-active-fg': dt.tokens['color-action-icon-subtle-selected'],
    'icon-active-bg': dt.tokens['color-action-background-subtle-selected'],
    'space-inline-s': dt.tokens['space-inline-s'],
    'icon-squish-s': dt.tokens['space-squish-s'],
    'min-width': `${opts?.minTabWidth}ch`,
    'icon-active-filter': props.orientation === 'vertical' && props.selected && dt.themeVariant === 'high-contrast' ? 'invert(1) grayscale(1)' : 'none',
    // If overrides are provided don't provide a hover-bg. The consumer can handle that if they want to.
    ...(opts?.tokenOverrides ? {
      'hover-bg': undefined
    } : undefined),
    ...opts?.tokenOverrides?.(dt, props)
  };
  return {
    ...renderDesignTokens(token, 'tab'),
    ...(!dt.enableNovaTokens && getTabLegacyDesignTokens(dt, opts))
  };
};
export const getTabLegacyDesignTokens = (dt, opts) => {
  const focusToken = 'var(--focus-light-bg)';
  let tokens;
  let reportToken;
  const props = {
    selected: opts?.selected ?? false,
    alternate: opts?.alternate ?? false,
    standardSmall: opts?.standardSmall ?? false,
    variant: opts?.variant ?? TabBarVariant.Standard,
    orientation: opts?.orientation ?? 'horizontal'
  };

  // Legacy Token Support
  switch (dt.baseReportThemeName) {
    case 'highcontrast2018':
    case 'highcontrast2017':
      reportToken = {
        bg: opts?.alternate && opts?.selected ? dt.c.black : opts?.orientation === 'vertical' && opts?.selected ? dt.reportTokens.stateStandard.selected.bg : 'transparent',
        fg: opts?.orientation === 'vertical' && opts?.selected ? dt.c.black : dt.c.white,
        bd: opts?.alternate ? dt.c.white : 'transparent',
        'selected-indicator': opts?.alternate ? dt.c.white : dt.c.hcStandardSelected,
        'disabled-bg': 'var(--tab-bg)',
        'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
        'indicator-content': opts?.alternate ? 'none' : '""',
        'indicator-width': '3px',
        focus: focusToken,
        'editable-hover-bd': 'var(--tab-fg)'
      };
      break;
  }
  switch (dt.themeVariant) {
    case 'dark':
      tokens = {
        bg: opts?.alternate && opts?.selected ? dt.c.p_3 : opts?.orientation === 'vertical' && opts.selected ? dt.reportTokens.stateStandard.selected.bg : 'transparent',
        fg: opts?.orientation === 'vertical' && opts?.selected ? dt.reportTokens.stateStandard.selected.fg : dt.c.white,
        bd: 'transparent',
        'selected-indicator': dt.c.white,
        'disabled-bg': 'var(--tab-bg)',
        'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
        'indicator-content': '""',
        'indicator-width': '1px',
        focus: focusToken,
        'hover-bg': dt.c.white20,
        'editable-hover-bd': 'var(--tab-fg)'
      };
      break;
    case 'high-contrast':
      tokens = {
        bg: opts?.alternate && opts?.selected ? dt.reportTokens.stateStandard.normal.bg : opts?.orientation === 'vertical' && opts?.selected ? dt.reportTokens.stateStandard.selected.bg : 'transparent',
        fg: opts?.orientation === 'vertical' && opts?.selected ? dt.reportTokens.stateStandard.selected.fg : dt.reportTokens.stateStandard.normal.fg,
        bd: dt.reportTokens.stateStandard.normal.bd,
        'selected-indicator': dt.reportTokens.stateStandard.normal.fg,
        'disabled-bg': 'var(--tab-bg)',
        'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
        'indicator-content': opts?.alternate ? 'none' : '""',
        'indicator-width': '3px',
        focus: focusToken,
        'hover-bg': dt.c.n_4,
        'editable-hover-bd': 'var(--tab-fg)'
      };
      break;
    default:
      tokens = {
        bg: opts?.alternate && opts?.selected ? dt.c.white : opts?.orientation === 'vertical' && opts?.selected ? dt.reportTokens.stateStandard.selected.bg : 'transparent',
        fg: opts?.orientation === 'vertical' && opts?.selected ? dt.reportTokens.stateStandard.selected.fg : dt.c.n_3,
        'count-indicator-fg': dt.tokens['color-text-neutral-inverse'],
        'icon-indicator-fg': dt.c.black,
        'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
        'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
        bd: 'transparent',
        'selected-indicator': dt.c.n_3,
        'indicator-content': '""',
        'indicator-width': '1px',
        focus: focusToken,
        'hover-bg': setAlpha(dt.c.n1, 0.2),
        'editable-hover-bd': 'var(--tab-fg)'
      };
  }
  return {
    ...renderDesignTokens({
      ...tokens,
      ...reportToken,
      'item-font-size': dt.reportTokens.font.size.m,
      'item-font-weight': dt.reportTokens.font.weight.default,
      'font-size-s': dt.reportTokens.font.size.s,
      'icon-hover-fg': dt.reportTokens.stateStandard.hover.fg,
      'icon-hover-bg': dt.reportTokens.stateStandard.hover.bg,
      'icon-active-fg': dt.reportTokens.stateStandard.active.fg,
      'icon-active-bg': dt.reportTokens.stateStandard.active.bg,
      'icon-active-filter': props.orientation === 'vertical' && props.selected && dt.themeVariant === 'high-contrast' ? 'invert(1) grayscale(1)' : dt.reportTokens.stateStandard.normal.filter,
      // If overrides are provided don't provide a hover-bg. The consumer can handle that if they want to.
      ...(opts?.tokenOverrides ? {
        'hover-bg': undefined
      } : undefined),
      ...opts?.tokenOverrides?.(dt, props)
    }, 'tab')
  };
};
//# sourceMappingURL=Tab.js.map